import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import PDWebPlayerRoot from '@vh/components/organisms/PDWebPlayerRoot/PDWebPlayerRoot';
import LoadingPageIndicator from '@base/components/LoadingPageIndicator';
import { useQuery } from '@apollo/client';
import PageLoadError from '@base/components/PageLoadError';
import { useLocation } from 'react-router-dom';
import { useRouterQuery } from '@core/helpers/useRouterQuery';
import DummyMicPermissionsComponent from '@vh/components/DummyMicPermissionsComponent/DummyMicPermissionsComponent';
import getConfig from '@base/lib/config';
import { READ_UNIT_MODULE } from '@base/queries/LearnerQueries';
import useUser from '@core/helpers/useUser';
import { useCurrentOrganisation } from '@core/helpers/useCurrentOrganisation';
import { useDeepLink } from '@core/components/DeepLink';

const PDWebPlayerPageContent = ({
  data,
  error,
  loading,
  isEmbedded,
  sharetoken,
  user: userParam,
  isPublicUserSession,
}) => {
  const query = useRouterQuery();
  const loggedUser = useUser();
  const { pathname } = useLocation();

  const user = userParam ?? loggedUser;

  const deepQuery = useMemo(() => {
    const search = new URLSearchParams({
      vhID: data?.readOneVirtualHuman?.ID,
      content: 'true',
      public: 'true',
      organisationID: 'null',
    });

    return search.toString();
  }, [data]);

  useDeepLink(
    data?.readOneVirtualHuman?.ID ? `${pathname}?${deepQuery}` : null
  );

  useCurrentOrganisation(data?.readOneVirtualHuman?.OrganisationID, isEmbedded);

  const unitID = query.get('unitID');
  const isPreviewPlayer = query.get('isPreviewPlayer') === 'true';

  const { data: dataUnit } = useQuery(READ_UNIT_MODULE, {
    skip: !unitID,
    variables: { ID: unitID },
  });

  const nextUnit = useMemo(() => {
    const units = (dataUnit?.readOneUnit?.Module?.Units?.edges ?? []).map(
      ({ node }) => node
    );

    const unitIndex = units.findIndex(
      ({ ID }) => String(ID) === String(unitID)
    );
    return units[unitIndex + 1];
  }, [dataUnit, unitID]);

  const courseID = useMemo(
    () => dataUnit?.readOneUnit?.Module?.CourseID ?? null,
    [dataUnit]
  );

  return (
    <>
      {/* use loading as a way to run the stream temporarily to ask for permisisons */}
      {loading && <DummyMicPermissionsComponent />}
      {loading && <LoadingPageIndicator />}
      {error && <PageLoadError graphQLErrorObj={error} />}
      {!loading && !error && data?.readOneVirtualHuman && (
        <PDWebPlayerRoot
          isPreviewPlayer={!!isPreviewPlayer}
          user={user}
          rootDirectory="/"
          unitID={unitID}
          courseID={courseID}
          nextUnit={nextUnit}
          // onSessionComplete={browseToVHList}
          virtualHumanData={data.readOneVirtualHuman}
          silverstripeEndPoint={getConfig('REACT_APP_REMOTE_URI')}
          isEmbedded={isEmbedded}
          sharetoken={sharetoken}
          isPublicUserSession={!!isPublicUserSession}
        />
      )}
    </>
  );
};

PDWebPlayerPageContent.propTypes = {
  user: PropTypes.shape({
    ID: PropTypes.string,
    Name: PropTypes.string,
  }),
  isPreviewPlayer: PropTypes.bool,
  isEmbedded: PropTypes.bool,
};

export default PDWebPlayerPageContent;
